<template>
  <model-form
    :model="hardware"
    modal-type="modal"
    width="600px;"
  >
    <div class="column">
      <div class="row q-col-gutter-sm items-start">
        <q-input
          v-model="hardware.name"
          class="col-6"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          :autofocus="!$q.platform.is.ios"
        />
        <q-select
          v-model="hardware.config.role"
          class="col-6"
          label="Role"
          :options="Hardware.roleOptions"
          map-options
          emit-value
          bottom-slots
        />
      </div>
      <div class="row q-col-gutter-sm items-start">
        <q-toggle
          v-model="hardware.config.wifiEnabled"
          class="col-4"
          label="WiFi Enabled"
          bottom-slots
        />
        <q-input
          v-model="hardware.config.wifiSsid"
          :disable="!hardware.config.wifiEnabled"
          class="col-4"
          label="WiFi SSID"
          bottom-slots
        />
        <q-input
          v-model="hardware.config.wifiPassword"
          :disable="!hardware.config.wifiEnabled"
          class="col-4"
          label="WiFi Password"
          bottom-slots
        />
      </div>
      <div
        v-if="hardware.config.role === 'register' || hardware.config.role === 'printer'"
        class="row q-col-gutter-sm items-start q-pb-md"
      >
        <q-checkbox
          v-if="hardware.config.role === 'register'"
          v-model="hardware.config.hasReceiptPrinter"
          class="col"
          label="Receipt Printer"
          bottom-slots
        />
        <q-input
          v-model.number="hardware.config.receiptPaperWidth"
          class="col"
          label="Printer Width (px)"
          type="number"
          min="100"
          max="1000"
          bottom-slots
        />
        <q-checkbox
          v-if="hardware.config.role === 'register'"
          v-model="hardware.config.hasRfidReader"
          class="col"
          label="RFID Reader"
          bottom-slots
        />
      </div>
      <div
        v-if="hardware.config.role === 'register'"
        class="row q-col-gutter-sm items-start q-pb-md"
      >
        <q-checkbox
          v-model="hardware.config.hasMagneticCardReader"
          class="col"
          label="Magnetic Card Reader"
          bottom-slots
        />
        <q-checkbox
          v-model="hardware.config.hasQrCodeReader"
          class="col"
          label="QR Code Reader"
          bottom-slots
        />
        <q-checkbox
          v-model="hardware.config.hasCashDrawer"
          class="col"
          label="Cash Drawer"
          bottom-slots
        />
      </div>
      <div v-if="hardware.config.role === 'display'">
        <div
          class="row q-col-gutter-sm items-start"
        >
          <q-checkbox
            v-model="hardware.config.disableOverscan"
            class="col-6"
            label="Disable Overscan"
            bottom-slots
          />
          <q-select
            v-model="hardware.config.displayRotateIndex"
            class="col-6"
            label="Display Rotation"
            :options="Hardware.displayRotationIndexOptions"
            map-options
            emit-value
            bottom-slots
          />
        </div>
        <signage-builder
          class="q-my-md"
          :display-url="hardware.config.displayUrl"
          :url-restart-enabled="true"
          :url-can-restart="!hardware.dirty"
          :display-rotation-index="hardware.config.displayRotateIndex"
          @restart-display="() => triggerAction('restart_display')"
          @url-changed="(url) => hardware.config.displayUrl = url"
        />
      </div>

      <div class="row q-gutter-md items-center">
        <q-btn
          size="sm"
          class="col-auto"
          label="Restart"
          outline
          color="negative"
          :loading="loadingAction === 'restart'"
          @click="triggerAction('restart')"
        >
          <q-tooltip>
            The will issue a command to restart the Raspberry Pi Software.
            <br><br>
            This will take approxomately 5 seconds to execute.
          </q-tooltip>
        </q-btn>
        <q-btn
          size="sm"
          class="col-auto"
          label="Reboot"
          outline
          color="negative"
          :loading="loadingAction === 'reboot'"
          @click="triggerAction('reboot')"
        >
          <q-tooltip>
            The will issue a command to reboot the Raspberry Pi Hardware, which
            is roughly identical to resetting power.
            This will only work if the Raspberry Pi is in the proper
            state and is connected.
            <br><br>
            An example of a time when this is needed is if the network
            the Raspberry Pi is running on changes or if there is a general
            issue with connecting or otherwise.
            <br><br>
            This will take approxomately 1 minute to execute.
          </q-tooltip>
        </q-btn>
        <q-btn
          size="sm"
          class="col-auto"
          label="Update Software"
          outline
          color="negative"
          :loading="loadingAction === 'update_software'"
          @click="triggerAction('update_software')"
        >
          <q-tooltip>
            The will issue a command to download the latest version of the
            INDY Software for the Raspberrry Pi, install it, and reboot.
            <br><br>
            This will take approxomately 3 minute to execute.
          </q-tooltip>
        </q-btn>
        <q-btn
          size="sm"
          class="col-auto"
          label="Clone SDs"
          outline
          color="negative"
          :loading="loadingAction === 'clone_sd_cards'"
          @click="triggerAction('clone_sd_cards')"
        >
          <q-tooltip>
            The will issue a command to clone the current running Raspberry Pi
            to any SD Cards mounted via USB ports. This will attempt to clone to
            up to 4 cards sequentially (/dev/sda, /dev/sdb, /dev/sdc, /dev/sdd).
            <br><br>
            This will take approxomately 20 minute PER CARD to execute. The only indication
            that the job is complete is the blinking on the SD Card reader will stop.
          </q-tooltip>
        </q-btn>
        <q-btn
          size="sm"
          class="col-auto"
          label="Open Tunnel"
          outline
          color="negative"
          :loading="loadingAction === 'open_tunnel'"
          @click="triggerAction('open_tunnel')"
        >
          <q-tooltip>
            The will issue a command to open a reverse SSH tunnel so the Raspberry Pi can be
            accessed remotely from the INDY Proxy Server
          </q-tooltip>
        </q-btn>
      </div>
    </div>
  </model-form>
</template>

<script>

import { Dialog } from 'quasar'
import { region } from 'src/config'
import Hardware from 'src/models/hardware/Hardware'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import SignageBuilder from 'src/modules/admin/signage/SignageBuilder'

export default {
  name: 'HardwareForm',
  components: {
    ModelForm,
    SignageBuilder
  },
  data () {
    return {
      loadingAction: null
    }
  },
  created () {
    _.extend(this, { Hardware, region })
  },
  methods: {
    async triggerAction (action) {
      this.loadingAction = action
      const success = await this.hardware.triggerAction(action)

      if (success) {
        Dialog.create({
          title: 'Command Sent!',
          message: 'The command succesfully sent to the hardware.' +
          (action === 'open_tunnel' ? ` Port ${25000 + this.hardware.id} opened on INDY Proxy server.` : '')
        })
      }
      this.loadingAction = null
    }
  },
  configuration: ['currentSite'],
  models: {
    hardware: {
      fields: ['id', 'name', 'config']
    }
  }
}

</script>
